import { computed, makeAutoObservable } from "mobx";
import {
  EmptyResourceResponseImpl,
  ObservableResourcePropImpl,
} from "../DTO/ServerResponseType/Resource/Resource";
import { SaveStoreApi } from "./API/SaveStore";
import ResourceServerClient from "../ServerClients/ResourceServerClient";
import {
  isResourceWithFileResponseDTO,
  ResourceWithContentResponseApi,
} from "../DTO/ServerResponseType/Resource/ResourceWithContent";
import ImageDTO from "../DTO/Image/ImageDTO";
import ImageConverter from "../utils/convert/ImageConverter";
import { CheckArgs } from "../utils/checks/CheckArgsWithoutThrow";
import { ResourceRequestImpl } from "../DTO/ClientRequest/Resource";
import { base64toFile } from "../utils/convert/base64ToFile";
import { ResourceVisibilityEnum } from "../Enums/ResourceVisibilityEnum";

export class ResourceStore implements SaveStoreApi {
  /**
   * Resource data without content
   */
  resource: ObservableResourcePropImpl = new ObservableResourcePropImpl(
    new EmptyResourceResponseImpl()
  );

  /**
   * Resource content = "file"
   */
  resourceFile?: ImageDTO;

  /**
   * Resource content = url
   */
  resourceLink?: string;

  resourceFormat: string = "";
  resourceIsDownloadable: boolean = false;

  withNavigationAfterSaving: boolean = true;

  constructor() {
    makeAutoObservable(this);
  }

  onChangeName = (event: React.FormEvent<HTMLInputElement>) => {
    this.resource.onChangeName(event.currentTarget.value);
  };

  onChangeLink = (event: React.FormEvent<HTMLInputElement>) => {
    this.resourceLink = event.currentTarget.value;
  };

  onChangeVisibility = (event: React.FormEvent<HTMLSelectElement>) => {
    let eventOption =
      event.currentTarget.options[event.currentTarget.selectedIndex];

    if (eventOption)
      this.resource.onChangeVisibility(
        eventOption.value === ResourceVisibilityEnum.All
      );
  };

  onChangeIsDownloadable = (event: React.FormEvent<HTMLInputElement>) => {
    this.resourceIsDownloadable = event.currentTarget.checked;
  };

  onChangeType = (event: React.FormEvent<HTMLSelectElement>) => {
    this.resource.onChangeType(
      parseInt(
        event.currentTarget.options[event.currentTarget.selectedIndex].value
      )
    );
  };

  onChangeFormat = (format: string) => {
    this.resourceFormat = format;
  };

  onChangeFileAsync = async (event: React.FormEvent<HTMLInputElement>) => {
    // File was not selected
    if (event.currentTarget.files === null) return;

    let file = event.currentTarget.files[0];

    this.resourceFile = new ImageDTO({
      id: this.resource.resourceId,
      data: file,
      src: URL.createObjectURL(file),
    });

    this.resourceFormat = file.type;
  };

  onChangeCost = (event: React.FormEvent<HTMLInputElement>) => {
    if (event.currentTarget.value !== "")
      this.resource.onChangeCost(parseInt(event.currentTarget.value));
    else this.resource.onChangeCost(0);
  };

  @computed get saveWindowTitle(): string {
    return this.resource.resourceId === ""
      ? "Создание ресурса"
      : "Обновление ресурса";
  }

  @computed get saveButtonText(): string {
    return this.resource.resourceId === ""
      ? "Создать ресурс"
      : "Обновить ресурс";
  }

  @computed get saveClickAsync() {
    let req = new ResourceRequestImpl(this.resource, {
      resourceFile: this.resourceFile,
      resourceLink: this.resourceLink,
      resourceFormat: this.resourceFormat,
      resourceIsDownloadable: this.resourceIsDownloadable,
    });
    let savePromise;
    if (CheckArgs.CheckNullOrUndefinedOrEmptyString(this.resource.resourceId)) {
      savePromise = ResourceServerClient.sendToCreateResourceAsync(req).then(
        (resourceId: string | null) => {
          if (resourceId !== null) {
            this.setId(resourceId);
          }

          return resourceId;
        }
      );
    } else {
      savePromise = ResourceServerClient.sendToUpdateResourceAsync(req);
    }

    return savePromise;
  }

  clearResourceContent() {
    this.resourceFile = undefined;
    this.resourceLink = undefined;
  }

  setResource = (resource: ResourceWithContentResponseApi) => {
    this.resource = new ObservableResourcePropImpl(resource);

    this.resourceFormat = resource.resourceFormat;
    this.resourceIsDownloadable = resource.resourceIsDownloadable;

    if (isResourceWithFileResponseDTO(resource))
      this.resourceFile = new ImageDTO({
        id: resource.resourceId,
        data: base64toFile(
          resource.resourceBufferString,
          resource.resourceName,
          resource.resourceFormat
        ),
        src: ImageConverter.AddConfigStringBeforeImageDataString(
          resource.resourceBufferString,
          resource.resourceFormat
        ),
      });
    else this.resourceLink = resource.resourceLink;
  };

  setId = (resourceId: string) => {
    this.resource.onChangeId(resourceId);
  };

  loadResourceWithContentAsync = async (
    resourceId: string
  ): Promise<ResourceWithContentResponseApi | never> => {
    return ResourceServerClient.sendToGetResourceWithContentByResourceIdAsync(
      resourceId
    ).then((resource) => {
      this.setResource(resource);
      return resource;
    });
  };

  loadNewResourceAsync = async (): Promise<EmptyResourceResponseImpl> => {
    this.resource = new ObservableResourcePropImpl(
      new EmptyResourceResponseImpl()
    );
    this.resourceFile = undefined;
    this.resourceLink = undefined;
    return new EmptyResourceResponseImpl();
  };
}
